import { logError } from 'src/utils/logger';

const humps = require('../../utils/humps');
const Facade = require('../abstract/facade');
const EditorFacade = require('../editor/editor');
const CompanyFactory = require('../../factory/company').instance;

function ProjectFacade () {
  this.handlers = {
    design: require('./handlers/design')
  };

  this.pageData = {};
  this._initListeners();
}

ProjectFacade.prototype = new Facade();
ProjectFacade.prototype.constructor = ProjectFacade;

ProjectFacade.prototype.get = function (elementType, attributeChain) {
  const functionName = humps.camelize('get_' + elementType);

  if (this[functionName]) {
    return this[functionName](attributeChain);
  }

  if (this.getters[elementType]) {
    return this.getters[elementType].get(attributeChain);
  }
};

ProjectFacade.prototype.checkDesignId = function () {
  const currentDesign = this.pageData[INKIVE.Tool._current_page_number - 1];
  if (currentDesign) {
    if (!currentDesign.design_id) {
      currentDesign.design_id = INKIVE.Tool._current_design_id;
    } else if (currentDesign.design_id !== INKIVE.Tool._current_design_id) {
      $.post(INKIVE.C.BASE_URL + 'api/errortrack/log', {
        is_critical: true,
        data: '[PageOrder] Design id missmatch ' +
          INKIVE.C.BOOK.id +
          ', expected: ' + INKIVE.Tool._current_design_id +
          ', got: ' + currentDesign.design_id +
          ', page number: ' + INKIVE.Tool._current_page_number
      });
    }
  } else {
    $.post(INKIVE.C.BASE_URL + 'api/errortrack/log', {
      is_critical: true,
      data: '[PageOrder] Design id not found ' +
        INKIVE.C.BOOK.id +
        ', expected: ' + INKIVE.Tool._current_design_id +
        ', page number: ' + INKIVE.Tool._current_page_number
    });
  }
};

ProjectFacade.prototype.getDesignId = function (params) {
  const deferred = $.Deferred();
  const design = this.getDesign(params);

  if (design && design.design_id) {
    deferred.resolve(design.design_id);
  } else {
    this.refreshPageInfo().then(_.bind(function () {
      const design = this.getDesign(params);
      deferred.resolve(design.design_id);
    }, this));
  }

  return deferred.promise();
};

ProjectFacade.prototype.getMasterBucket = function () {
  return this.masterBucket;
};

ProjectFacade.prototype.getDesign = function (params) {
  const designs = this.getDesigns();

  return params.page_number
    ? designs[params.page_number - 1]
    : _.find(designs, function (item) {
      return item.design_id === String(params.design_id);
    });
};

ProjectFacade.prototype.getDesigns = function () {
  return _.map(this.pageData, function (item, index) {
    return _.extend(_.clone(item), { page_number: index + 1 });
  });
};

ProjectFacade.prototype._initListeners = function () {
  EditorFacade.on('page.saved', _.bind(function () {
    const design = this.pageData[INKIVE.Tool._current_page_number - 1];
    if (design) design.last_mod = Date.now();
  }, this));

  this.on('design.removing', _.bind(function (data) {
    const design = this.getDesign({ design_id: data.design_id });
    const page = design ? parseInt(design.page_number) : data.page_number;

    if (this.pageData && this.pageData.length >= page) {
      this.pageData.splice(page - 1, 1);
    }
  }, this));

  this.on('design.added', _.bind(function (data) {
    const designId = Number(data.id || data.design_id);
    const matchedDesignIndex = this.pageData.findIndex(item => getDesignId(item) === designId);
    const index = matchedDesignIndex > -1 ? matchedDesignIndex : data.page_number - 1;
    const deleteCount = matchedDesignIndex > -1 ? 1 : 0;

    this.pageData && this.pageData.splice(index, deleteCount, {
      id: designId,
      design_id: data.design_id,
      size_width: data.size.width,
      size_height: data.size.height,
      size_unit: data.size.unit
    });
  }, this));

  this.on('design.copied', _.bind(function (data) {
    const originalDesign = this.getDesign({ design_id: data.based_on });

    this.pageData && this.pageData.splice(data.page_number - 1, 0, _.extend(originalDesign, {
      design_id: data.design_id,
      id: Number(data.design_id),
      based_on: data.based_on
    }));
  }, this));

  function getDesignId (data) {
    return Number(data.id || data.design_id);
  }
};

ProjectFacade.prototype.getInfo = function (params) {
  if (params && params.forceReload) {
    this.pageInfoDeferred = null;
  }

  const deferred = $.Deferred();
  const promise = this.loadPageInfo();

  promise.then(_.bind(function () {
    this.trigger('get_info', this.pageInfo);
    deferred.resolve(this.pageInfo);
  }, this));

  return deferred.promise();
};

ProjectFacade.prototype.loadPageInfo = function () {
  if (!this.pageInfoDeferred) {
    const onLoad = _.after(2, _.bind(function () {
      this.pageInfoDeferred.resolve(this.pageInfo);
    }, this));

    this.pageInfo = this.pageInfo || {};
    this.pageInfoDeferred = $.Deferred();

    CompanyFactory.fetchProjectData().then(projectData => {
      this.pageInfo.is_multipage = (projectData && (typeof projectData.is_multipage !== 'undefined'))
        ? projectData.is_multipage
        : true;

      if (projectData) {
        projectData.company && _.extend(this.pageInfo, { company: projectData.company });
        projectData.template && _.extend(this.pageInfo, { template: projectData.template });
        this.pageInfo.is_printable = projectData.is_printable;

        projectData.id && _.extend(this.pageInfo,
          INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE
            ? { template_id: projectData.id }
            : { design_id: projectData.id }
        );
      }

      const pages = addPageNumber(projectData?.pages || []);

      this.pageData = pages;
      this.pageInfo.page_info = pages;
      this.pageInfo.name = projectData?.name ?? this.pageInfo.name;
      INKIVE.C.BOOK.page_info = pages;
      this.hasPageData = true;

      this.trigger('project.fetched', projectData);
      this.trigger('pages.fetched', pages);

      INKIVE.C.BOOK.num_pages = parseInt(this.pageData.length);
      onLoad();
    });

    const url = INKIVE.C.BASE_URL + 'inkive/doescoverpageexist';
    $.api(url, 'POST', { scrapbook_id: INKIVE.C.BOOK.id }, (data) => {
      _.extend(this.pageInfo, data);
      this.masterBucket = INKIVE.Helpers.getBucketUrl(data.master_bucket);
      INKIVE.C.BOOK.name = Base64.encode(data.name);
      onLoad();
    });
  }

  return this.pageInfoDeferred.promise();
};

ProjectFacade.prototype.refreshPageInfo = function () {
  // TODO: Lost order exception
  this.pageInfoDeferred = null;

  return CompanyFactory.fetchProjectData()
    .then(data => {
      const pages = addPageNumber(data.pages);

      this.pageData = pages;
      this.pageInfo.page_info = pages;

      return data;
    })
    .then(_.bind(this.trigger, this, 'refresh_page_info'), logError('refreshPageInfo'));
};

// quickRefreshPageInfo is a trimmed down version of refreshPageInfo above where we don't need to fetch the project data (because page order is already updated)
ProjectFacade.prototype.quickRefreshPageInfo = function () {
  return new Promise((resolve) => {
    this.trigger('refresh_page_info', this.pageData);
    resolve();
  });
};

ProjectFacade.prototype.changePageOrder = async function (newOrder, newerIndex, isQuickRefresh) {
  const deferred = $.Deferred();
  const pageData = [...this.pageData];
  const pages = newOrder.map(id => ({ id }));
  const previousOrder = INKIVE.ProjectFacade.get('designs').map(page => ({
    id: page.id,
    pageNumber: page.page_number
  }));
  const movedPage = Number(newOrder[newerIndex]);
  const [first, last] = newOrder.map(Number).reduce((acc, next, index) => {
    if (acc.length === 0 && next !== previousOrder[index]?.id) return acc.concat(index + 1);
    if (acc.length > 0 && next !== previousOrder[index]?.id) return [acc[0], index + 1];
    return acc;
  }, []);
  const [previousPageNumber, nextPageNumber] = (newerIndex + 1) === last ? [first, last] : [last, first];

  this.trigger('change_page_order', newOrder);

  if (isQuickRefresh) {
    const newPageData = getNewPageData(this.pageData, newOrder);

    this.pageData = newPageData;
    this.pageInfo.page_info = newPageData;
  }

  const onOrderChanged = () => {
    const refreshFunc = isQuickRefresh ? 'quickRefreshPageInfo' : 'refreshPageInfo';

    const onPageInfoRefresh = async () => {
      if (typeof newerIndex !== 'undefined') {
        await EditorFacade.handle('header_and_footer', 'order_changed', {
          previousPageNumber,
          nextPageNumber,
          pageId: movedPage,
          previousOrder
        });
      }

      INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE
        ? mapRestrictions.call(this)
        : done.call(this);
    };

    this[refreshFunc]()
      .then(onPageInfoRefresh)
      .catch(() => {
        throw new Error('Failed to refresh page info');
      });
  };

  try {
    const result = await INKIVE.PageFactory.saveProjectData({ pages });

    if (result.status !== 'success') {
      throw new Error('Failed to change page order');
    }

    onOrderChanged();
  } catch (e) {
    this.pageData = pageData;
    this.pageInfo.page_info = pageData;

    this.trigger('change_page_order', previousOrder.map(page => String(page.id)));

    throw e;
  }

  function mapRestrictions () {
    const RestrictionsFactory = require('../../factory/restrictions');

    const updateRules = pageData
      .map(oldDesign => {
        const newDesign = this.pageData.find(newDesign => newDesign.design_id === oldDesign.design_id);
        return this.pageData.indexOf(newDesign);
      })
      .map((newPosition, originalPosition) =>
        originalPosition !== newPosition
          ? { from: originalPosition + 1, to: newPosition + 1 }
          : null
      )
      .filter(Boolean);

    const modelsToBeUpdate = [];

    RestrictionsFactory.getElementPermissionsCollection().each(function (model) {
      const updateRule = _.find(updateRules, function (ur) {
        return ur.from === model.get('page_number');
      });

      updateRule && modelsToBeUpdate
        .push(_.extend({ model }, updateRule));
    });

    (function updateModel () {
      const info = modelsToBeUpdate.pop();

      if (info) {
        info.model.set('page_number', info.to);
        info.model.save().then(_.bind(updateModel, this));
      } else {
        done.call(this);
      }
    }).call(this);
  }

  function done () {
    const design = this.get('design', { design_id: INKIVE.Tool._current_design_id });
    INKIVE.Tool._current_page_number = design.page_number;
    deferred.resolve();
  }
};

function addPageNumber (pages) {
  return pages.map((page, index) => ({
    ...page,
    page_number: index + 1
  }));
}

function getNewPageData (currentPageData, newOrder) {
  const designByDesignID = new Map();

  currentPageData.forEach(page => {
    designByDesignID.set(page.design_id, page);
  });

  return newOrder.map(designID => designByDesignID.get(designID));
}

module.exports = new ProjectFacade();
